/**
 * The run page's main column: what is happening now in one bright line, then whatever needs a
 * person answered right here (requests, questions, a blocked session), then the followed session's
 * live activity, the same timeline as the session drawer. Nothing here navigates to the Inbox.
 */
import { CircleCheck, CircleDashed, CircleX, Hand, LoaderCircle, ShieldAlert, Eye, CircleStop } from '@orgus/ui/icons';
import type { ComponentType } from 'react';
import { useSearchParams } from 'react-router';
import type { Session } from '@orgus/protocol';
import { Panel, RelativeTime, cn } from '@orgus/ui';
import { useControlState } from '../../control/live.ts';
import { SessionMessage } from '../../control/SessionControl.tsx';
import { SessionApprovals } from '../../approvals/SessionApprovals.tsx';
import { ApprovalBlocker } from '../../approvals/ApprovalBlocker.tsx';
import { ApprovalEvents } from '../../approvals/ApprovalExpiredEvent.tsx';
import { useApprovalBlock } from '../../approvals/blocked.ts';
import { FailureCard } from '../../sessions/FailureCard.tsx';
import { failureLine, failureOf } from '../../sessions/failures.ts';
import { Transcript } from '../../sessions/Transcript.tsx';
import { WaitingCallout } from '../../session-detail/Waiting.tsx';
import { MessageEntry } from '../../session-detail/MessageEntry.tsx';
import { Messages } from '../../session-detail/Messages.tsx';
import { useMessages } from '../../session-detail/api.ts';
import { useSessionControl } from '../../session-detail/DetailTabs.tsx';
import { plainText } from '../../../lib/plainText.ts';
import type { NowTone, RunPhase } from './model.ts';

/** Where the header's Answer button takes focus: the first control of the inline answers. */
export const ANSWER_SELECTOR = '[data-run-answer] button, [data-run-answer] textarea';

/**
 * What the header's Answer reaches, in order: a request waiting on a decision (its buttons), then
 * the message box (an enabled textarea, so the person can type at once), then any other control of
 * the inline answers (a terminal link, a failure action). QA-15: the first button in document
 * order used to win, so Answer only scrolled to a callout and never opened the reply box.
 */
export function answerTarget(root: ParentNode = document): HTMLElement | null {
  return (
    root.querySelector<HTMLElement>('[data-run-answer="requests"] button:not([disabled])') ??
    root.querySelector<HTMLElement>('[data-run-answer] textarea:not([disabled])') ??
    root.querySelector<HTMLElement>(ANSWER_SELECTOR)
  );
}

/** Scroll to the first thing to answer on the page and put focus on it; false when there is none here. */
export function focusAnswer(root: ParentNode = document): boolean {
  const el = answerTarget(root);
  if (!el) return false;
  el.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  el.focus({ preventScroll: true });
  return true;
}

const NOW_ICON: Record<RunPhase, ComponentType<{ className?: string }>> = {
  planned: CircleDashed,
  preparing: LoaderCircle,
  working: LoaderCircle,
  needs: Hand,
  blocked: ShieldAlert,
  checking: LoaderCircle,
  review: Eye,
  landing: LoaderCircle,
  landed: CircleCheck,
  done: CircleCheck,
  failed: CircleX,
  stopped: CircleStop,
};

const NOW_FG: Record<NowTone, string> = {
  working: 'text-status-working',
  attention: 'text-status-attention',
  danger: 'text-status-danger',
  success: 'text-status-success',
  neutral: 'text-fg-subtle',
};

/** The one bright line: an icon in the state's tone, the words, and when it last changed. */
export function NowLine({ phase, text, tone, at }: { phase: RunPhase; text: string; tone: NowTone; at: number | null }) {
  const I = NOW_ICON[phase];
  const spin = phase === 'preparing' || phase === 'working' || phase === 'checking' || phase === 'landing';
  return (
    <div className="flex min-w-0 items-start gap-3" role="status" aria-live="polite" data-run-now={phase}>
      <span className="flex h-6 shrink-0 items-center">
        <I className={cn('size-4', NOW_FG[tone], spin && 'motion-safe:animate-spin')} aria-hidden />
      </span>
      {/* Never cut: a failure's or an ask's last words say what to do next, and the working line is the one thing to read. */}
      <p className="min-w-0 flex-1 text-lead font-medium text-fg">{plainText(text)}</p>
      {at != null && <RelativeTime ts={at} className="flex h-6 shrink-0 items-center text-caption text-fg-subtle" />}
    </div>
  );
}

/**
 * The followed session's activity, as the session drawer's Activity tab shows it: a failure card,
 * the question it waits on, its requests and blocks answered in place, then its timeline, and the
 * message box when the session takes messages.
 */
function SessionActivity({ s, machineName, settled, failureSaid }: { s: Session; machineName: string; settled: boolean; failureSaid: boolean }) {
  const [, setParams] = useSearchParams();
  const controlled = !!useControlState()?.machines[s.machineId]?.installed;
  const { control } = useSessionControl(s);
  const composer = !controlled && !!control?.showComposer;
  const messages = useMessages(s, composer);
  const block = useApprovalBlock(s);
  const blocked = !!block && block.resumable && block.phase !== 'resuming';
  const openTerminal = () =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        n.set('session', s.id);
        n.set('tab', 'terminal');
        return n;
      },
      { preventScrollReset: true },
    );
  return (
    <div className="flex flex-col gap-4">
      <div data-run-answer className="flex flex-col gap-3 empty:hidden">
        {settled ? (
          <PastFailure s={s} />
        ) : (
          // The headline above already says what failed and what to do: here the card keeps its
          // label and its actions, and drops its own sentence (the failure is said once).
          <div className={cn('empty:hidden', failureSaid && '[&_[role=group]>p:first-of-type]:hidden')}>
            <FailureCard session={s} onOpenTerminal={openTerminal} />
          </div>
        )}
        {s.status === 'waiting' && <WaitingCallout session={s} machineName={machineName} composer={composer} controlled={controlled} liveTerminal={false} onOpenTerminal={openTerminal} />}
        <div className="-mx-4 -mt-4 empty:hidden">
          <ApprovalBlocker session={s} why={false} />
        </div>
        {composer && s.status !== 'ended' && <MessageEntry session={s} data={messages} blocked={blocked} />}
        <SessionMessage session={s} />
      </div>
      <ApprovalEvents session={s} />
      <Transcript session={s} surface="card" />
      {composer && (
        <div className="border-t border-border-subtle pt-4 empty:hidden">
          <Messages session={s} data={messages} />
        </div>
      )}
    </div>
  );
}

/**
 * A failed session of a mission that has since landed or closed: history, so a quiet line with no
 * action (resuming it would undo nothing and help nothing).
 */
function PastFailure({ s }: { s: Session }) {
  const f = failureOf(s);
  if (!f || f.cause === 'blocked') return null;
  return (
    <p className="flex min-w-0 items-center gap-2 text-caption text-fg-subtle">
      <CircleX className="size-3.5 shrink-0 text-status-danger" aria-hidden />
      <span className="text-fg-muted">Session failed</span>
      {f.cause !== 'crash' && <span className="min-w-0 truncate">{failureLine(f)}</span>}
    </p>
  );
}

export function RunStream({
  sessionIds,
  focus,
  focusTitle,
  machineName,
  emptyLine,
  settled = false,
  failureSaid = false,
}: {
  sessionIds: readonly string[];
  focus: Session | null;
  focusTitle: string | null;
  machineName: string;
  emptyLine: string;
  /** The mission landed or closed: a past failure is history, not a call to act. */
  settled?: boolean;
  /** The headline already states the session's failure: its card keeps only its label and actions. */
  failureSaid?: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* Requests from any of the mission's sessions, answered in place (they stay a moment as answered). */}
      <div data-run-answer="requests" className="flex flex-col gap-2 empty:hidden">
        <SessionApprovals sessionIds={sessionIds} />
      </div>
      <Panel title="Activity" sub={focusTitle ?? undefined}>
        {focus ? <SessionActivity s={focus} machineName={machineName} settled={settled} failureSaid={failureSaid} /> : <p className="text-ui text-fg-subtle">{emptyLine}</p>}
      </Panel>
    </div>
  );
}
